<!-- Ported from packages/website/src/components/DatePicker/Month.vue -->
<template>
  <div :data-testid="`month-${month.getFullYear()}-${month.getMonth()}`">
    <div class="mb-1 grid grid-cols-[2rem_1fr_2rem] items-center">
      <div><slot name="previous" /></div>
      <button
        type="button"
        class="mx-auto rounded-full px-2 py-0.5 text-center text-sm font-bold uppercase text-hazel-500 hover:bg-obsidian-100"
        :data-testid="`datepicker-title-${index}`"
        @click="$emit('titleClick')"
      >
        {{ month.toLocaleString([locale], { month: 'long' }) }}
        {{ month.getFullYear() !== new Date().getFullYear() ? ` ${month.getFullYear()}` : '' }}
      </button>
      <div><slot name="next" /></div>
    </div>
    <div class="grid text-center text-xs font-bold" :class="gridClass">
      <div v-if="weekNumbers" class="p-2 text-obsidian-400">{{ t('calendarWeek') }}</div>
      <div v-for="(_, dayIndex) in 7" :key="dayIndex" class="p-2">
        {{ getDayName(dayIndex) }}
      </div>
    </div>
    <div class="w-full space-y-0.5">
      <div
        v-for="(week, weekIndex) in weeksInMonth"
        :key="weekIndex"
        class="grid"
        :class="gridClass"
      >
        <div
          v-if="weekNumbers"
          class="flex items-center justify-center p-2 text-xs text-obsidian-400"
        >
          {{ getISOWeek(week[0].date) }}
        </div>
        <div
          v-for="(day, dayIndex) in week"
          :key="dayIndex"
          :data-testid="
            day.isInMonth
              ? `day-${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`
              : undefined
          "
          :tabindex="day.isInMonth && day.enabled ? 0 : undefined"
          :aria-disabled="day.isInMonth && !day.enabled ? true : undefined"
          :class="{
            day: day.isInMonth,
            disabled: !day.enabled,
            highlighted: day.isInMonth && isDayHighlighted?.(day.date),
            today: day.isInMonth && isToday(day.date),
            inbetween:
              day.isInMonth &&
              startDate &&
              endDate &&
              isBefore(startDate, endDate) &&
              isWithinInterval(day.date, { start: startOfDay(startDate), end: endDate }) &&
              !isSameDay(day.date, startDate) &&
              !isSameDay(day.date, endDate),
            'start-date': day.isInMonth && startDate && isSameDay(day.date, startDate),
            'end-date': day.isInMonth && endDate && isSameDay(day.date, endDate),
          }"
          @click.prevent="handleDaySelect(day)"
          @keydown.enter.prevent="handleDaySelect(day)"
          @mouseover="day.isInMonth && $emit('dayMouseOver', day.date)"
          @mouseout="day.isInMonth && $emit('dayMouseOut')"
        >
          <span v-if="day.isInMonth">
            {{ day.date.getDate() }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import {
  addDays,
  getISOWeek,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import { useI18n } from 'vue-i18n';

interface Day {
  date: Date;
  enabled: boolean;
  isInMonth: boolean;
}

const props = defineProps<{
  month: Date;
  index: number;
  startDate?: Date | null;
  endDate?: Date | null;
  weekNumbers?: boolean;
  isDayDisabled?: (date: Date) => boolean;
  isDayHighlighted?: (date: Date) => boolean;
}>();

const emit = defineEmits<{
  (e: 'daySelected', date: Date): void;
  (e: 'dayMouseOver', date: Date): void;
  (e: 'dayMouseOut'): void;
  (e: 'titleClick'): void;
}>();

const { t, locale } = useI18n();

const gridClass = computed(() => (props.weekNumbers ? 'grid-cols-8' : 'grid-cols-7'));

const weeksInMonth = computed(() => {
  const lastDayOfMonth = endOfMonth(props.month);
  const weeks: Day[][] = [];
  let date = startOfISOWeek(startOfMonth(props.month));

  while (!isBefore(lastDayOfMonth, date)) {
    const week: Day[] = [];
    for (let i = 0; i < 7; i++) {
      const isInMonth = isSameMonth(date, props.month);
      week.push({ date, isInMonth, enabled: isInMonth && !props.isDayDisabled?.(date) });
      date = addDays(date, 1);
    }
    weeks.push(week);
  }

  return weeks;
});

const handleDaySelect = (day: Day) => {
  if (!day.isInMonth || !day.enabled) return;
  emit('daySelected', day.date);
};

const getDayName = (weekDay: number) => {
  return Intl.DateTimeFormat([locale.value ?? 'en'], {
    weekday: 'short',
  }).format(new Date(2020, 0, 6 + weekDay));
};
</script>

<style scoped lang="scss">
.day {
  @apply relative cursor-pointer p-2 text-center text-sm;

  &::before {
    @apply rounded-lg;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
  }

  & span {
    position: relative;
    z-index: 2;
  }

  &:not(.disabled):not(.start-date):not(.end-date):hover::before {
    @apply bg-obsidian-100;
  }

  &.today {
    @apply font-bold;
  }

  &.highlighted::before {
    @apply bg-bittersweet-100;
  }

  &.disabled {
    @apply cursor-default text-obsidian-400;
  }

  &.start-date::before {
    @apply rounded-r-none;
  }

  &.end-date::before {
    @apply rounded-l-none;
  }

  &.start-date.end-date::before {
    @apply rounded-lg;
  }

  &.start-date,
  &.end-date {
    @apply text-white;

    &::before {
      @apply bg-obsidian-600;
    }
  }

  &.inbetween {
    @apply bg-obsidian-100 lg:bg-obsidian-200;
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "calendarWeek": "CW"
  },
  "de": {
    "calendarWeek": "KW"
  },
  "es": {
    "calendarWeek": "Sem"
  }
}
</i18n>
